import type { FormInst, FormValidationError } from "naive-ui";
import { defineStore } from "pinia";
import { reactive, ref } from "vue";
import { authRepository } from "../../data/repositories/authRepository";

export const useAddAccountStore = defineStore("addAccountStore", () => {
  const isLoading = ref<boolean>(true);
  const errorMessage = ref<string | null>(null);
  const clientName = ref<string>("");
  const clientDescription = ref<string>("");
  const clientLogo = ref<string | null>(null);
  const isClientValid = ref<boolean>(false);

  // Simpan clientId dan redirectUri agar bisa dipakai saat login
  const clientId = ref<string>("");
  const redirectUri = ref<string>("");

  const formRef = ref<FormInst | null>(null);

  function clearAllOtpTimers() {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("mfa_otp_expires_")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  const form = reactive({
    isSubmitting: false,
    errorMessage: null as string | null,
    model: {
      email: "",
      password: "",
    },
    rules: {
      email: [
        {
          required: true,
          trigger: ["blur", "input", "change"],
          validator: (_: any, value: string) => {
            if (!value) {
              return new Error("Email required");
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            if (!emailRegex.test(value)) {
              return new Error("Email invalid");
            }

            return true;
          },
        },
      ],
      password: [
        {
          required: true,
          trigger: ["blur", "input", "change"],
        },
      ],
    },
    submit: (e: Event, onSuccess?: (res: any) => void) => {
      e.preventDefault();

      if (formRef.value) {
        formRef.value.validate(async (errors: Array<FormValidationError> | undefined) => {
          if (errors) {
            return;
          }

          form.isSubmitting = true;
          form.errorMessage = null;

          clearAllOtpTimers();

          try {
            const response = await authRepository.login({
              email: form.model.email,
              password: form.model.password,
              clientId: clientId.value,
              redirectUri: redirectUri.value,
            });

            if (onSuccess) {
              onSuccess(response);
            }
          } catch (error: any) {
            form.errorMessage = error.response?.data?.message || "Gagal melakukan login dengan akun ini.";

            window.$message.warning(form.errorMessage!);
          } finally {
            form.isSubmitting = false;
          }
        });
      }
    },
  });

  async function validateClient(cId: string, rUri: string) {
    isLoading.value = true;
    errorMessage.value = null;

    // Simpan nilainya ke state store
    clientId.value = cId;
    redirectUri.value = rUri;

    clearAllOtpTimers();

    try {
      const response = await authRepository.validateClient(cId, rUri);

      isClientValid.value = response.isValid;
      clientName.value = response.client.name;
      clientDescription.value = response.client.description ?? "";
      clientLogo.value = response.client.logo || null;
    } catch (error: any) {
      isClientValid.value = false;
      errorMessage.value = error.response?.data?.message || "Gagal memvalidasi klien atau Redirect URI tidak cocok.";
    } finally {
      isLoading.value = false;
    }
  }

  function resetForm() {
    formRef.value?.restoreValidation();

    form.isSubmitting = false;
    form.errorMessage = null;
    form.model.email = "";
    form.model.password = "";
  }

  function init() {
    isLoading.value = true;
    errorMessage.value = null;
    clientName.value = "";
    clientDescription.value = "";
    clientLogo.value = null;
    isClientValid.value = false;
    clientId.value = "";
    redirectUri.value = "";

    formRef.value = null;

    form.isSubmitting = false;
    form.errorMessage = null;
    form.model.email = "";
    form.model.password = "";

    clearAllOtpTimers();
  }

  return {
    isLoading,
    errorMessage,
    clientName,
    clientDescription,
    clientLogo,
    isClientValid,
    validateClient,
    form,
    formRef,
    init,
    resetForm,
  };
});
